/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import VineyardMapSvg from './vineyard-map-svg';
import VineyardMapText from './vineyard-map-text';
import { IVineyardMapData } from './vineyard-map.data';
import { IVineyardMapProps } from './vineyard-map.props.autogenerated';

/**
 *
 * VineyardMap component: Renders interactive map displaying different vineyard
 * areas that can be clicked to show corresponding information
 * @extends {React.Component<IVineyardMapProps<IVineyardMapData>>}
 */

@observer
class VineyardMap extends React.Component<IVineyardMapProps<IVineyardMapData>> {

    // =========================================================================
    // OBSERVABLES
    // =========================================================================

    /**
     * tracks current active vineyard area
     */
    @observable private activeArea: number = 0;

    /**
     * toggle auto advance active area functionality
     */
    private advance: boolean = true;

    /**
     * time in milliseconds for auto advance active area
     */
    private timer: number = 10000;

    /**
     * stores interval from auto advance set interval function
     */
    private interval: number = 0;

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================

    public constructor(props: IVineyardMapProps<IVineyardMapData>) {
        super(props);
        this._toggle = this._toggle.bind(this);
        this._autoAdvance = this._autoAdvance.bind(this);
        this._assignActiveSvg = this._assignActiveSvg.bind(this);
    }

    public componentDidMount(): void {
        this.advance
            ? this.interval = window.setInterval(this._autoAdvance, this.timer)
            : null;
    }

    public componentWillUnmount(): void {
        this.advance
            ? window.clearInterval(this.interval)
            : null;
    }

    public render(): JSX.Element {
        return (
            <div className='vineyard-map'>
                <VineyardMapSvg
                    toggle={this._toggle}
                    assignActiveSvg={this._assignActiveSvg}
                />
                <VineyardMapText
                    activeArea={this.activeArea}
                    config={this.props.config}
                    context={this.props.context}
                />
            </div>
        );
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================

    /**
     * toggles active vineyard area clicked by the user, returns undefined
     */
    private _toggle(area: number): undefined {
        this.activeArea = area;
        return undefined;
    }

    /**
     * auto advances active vineyard area until back at 1, returns undefined
     */
    private _autoAdvance(): undefined {
        if (this.activeArea === 7) {
            this.activeArea = 1;
            window.clearInterval(this.interval);
        } else {
            this.activeArea = this.activeArea + 1;
        }
        return undefined;
    }

    /**
     * assigns a CSS class to active vineyard area, returns a string
     */
    private _assignActiveSvg(area: number): string {
        if (area !== 7) {
            return this.activeArea === area
                ? 'vineyard-map-area-active'
                : '';
        } else {
            return this.activeArea === area
                ? 'vineyard-map-area-active vineyard-map-area-exception'
                : 'vineyard-map-area-exception';
        }
    }
}

export default VineyardMap;
